// src/stores/useAuthStore.ts
import { ref, computed, type Ref, type ComputedRef } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';
import { useToast } from '@fareharbor-com/beacon-vue';
import { defineStore } from 'pinia';
import { Logger } from '@/common/Logger';
import type { GenericResponse } from '@/common/api/base';
import { authTokenService } from '@/common/authTokenService';
import { FORBIDDEN, OK, UNAUTHORIZED } from '@/common/constants/status';
import { handleThrownError } from '@/common/errors/handleThrownError';
import type { LoginOptions, TokenPayload } from '@/types';

export interface AuthStore {
  hasTokens: Ref<boolean>;
  loginOptions: Ref<LoginOptions>;
  redirectRoute: Ref<RouteLocationNormalized | null>;
  isAuthenticated: ComputedRef<boolean>;
  logout(): void;
  obtainTokens(
    username: string,
    password: string,
  ): Promise<GenericResponse<TokenPayload> | null>;
  doTokenRefresh(): Promise<boolean>;
  setLoginOptions(options?: LoginOptions): void;
  setRedirectRoute(route: RouteLocationNormalized): void;
}

export const useAuthStore = defineStore('auth', (): AuthStore => {
  const hasTokens = ref<boolean>(authTokenService.hasTokens());
  const loginOptions = ref<LoginOptions>({});
  const redirectRoute = ref<RouteLocationNormalized | null>(null);

  const { showToast } = useToast();

  const isAuthenticated = computed(() => hasTokens.value);

  function logout() {
    authTokenService.clearTokens();
    localStorage.clear();
    hasTokens.value = false;
  }

  async function obtainTokens(
    username: string,
    password: string,
  ): Promise<GenericResponse<TokenPayload> | null> {
    try {
      const response = await authTokenService.obtainTokens(username, password);

      if (response.status === OK && response.data) {
        hasTokens.value = true;
        showToast('Login complete—enjoy your session!');
        return response;
      }

      if (response.status === UNAUTHORIZED || response.status === FORBIDDEN) {
        logout();
      }

      Logger.info('Unexpected error during token obtainment', {
        status: response.status,
      });
      return response;
    } catch (error) {
      handleThrownError({
        error,
        toastMessage: 'Unexpected error occurred during login.',
        log: true,
      });
      return null;
    }
  }

  async function doTokenRefresh(): Promise<boolean> {
    Logger.debug('Attempting token refresh …');
    try {
      const isRefreshed = await authTokenService.refreshAccessToken();
      if (isRefreshed) {
        hasTokens.value = true;
        Logger.debug('Token refreshed successfully!');
      } else {
        Logger.info('Could not refresh token!');
        logout();
      }
      return isRefreshed;
    } catch (error) {
      handleThrownError({
        error,
        log: true,
        toastMessage: 'Something went wrong with token refresh.',
      });
      return false;
    }
  }

  function setLoginOptions(options?: LoginOptions) {
    loginOptions.value = options ?? {};
  }

  function setRedirectRoute(route: RouteLocationNormalized) {
    redirectRoute.value = route;
  }

  return {
    hasTokens,
    loginOptions,
    redirectRoute,
    isAuthenticated,
    logout,
    obtainTokens,
    doTokenRefresh,
    setLoginOptions,
    setRedirectRoute,
  };
});
